import React, { useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, NumberInput, Message } from 'rsuite';
import { AxiosError } from 'axios';
import ErrorMessage from '@/components/ErrorMessage';
import Textarea from '@/components/Textarea';
import { BaseErrorModel } from '@/models/core.model';
import { EnergyHubCreditRequest, formatHkd } from '@/models/energy-hub.model';
import { EnergyHubCreditAction, postEnergyHubCredit } from '@/services/energy-hub.service';
import { toastSuccessMessage } from '@/utils/toasterMessage';

const titles: Record<EnergyHubCreditAction, string> = {
  TopUp: 'Top up credit',
  Refund: 'Refund credit',
  Adjust: 'Adjust credit',
};

const buildSchema = (action: EnergyHubCreditAction, balance: number) => Schema.Model<Record<string, any>>({
  amount: action == 'Adjust'
    ? Schema.Types.NumberType().isRequired('This field is required.')
      .addRule(v => Number(v) != 0, 'Amount cannot be 0.')
    : Schema.Types.NumberType().isRequired('This field is required.').min(0.01, 'Amount must be positive.')
      .addRule(v => action != 'Refund' || Number(v) <= balance, `Refund cannot exceed the balance (${formatHkd(balance)}).`),
  // Top-ups take an optional note (e.g. FPS reference); refunds and adjustments must say why.
  note: action == 'TopUp'
    ? Schema.Types.StringType().maxLength(1000)
    : Schema.Types.StringType().isRequired('Say why.').maxLength(1000),
});

const newIdempotencyKey = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface Props extends DrawerProps {
  action: EnergyHubCreditAction;
  energyHubId: number;
  balance: number;
  onSubmitted?: () => void;
}

const CreditDrawer = (props: Props) => {
  const { open, onClose, onSubmitted, action, energyHubId, balance, ...rest } = props;
  const [formValue, setFormValue] = useState<EnergyHubCreditRequest>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  // One key per drawer opening: a double-click / retry of the same submission is deduplicated by the API.
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey());
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  React.useEffect(() => {
    if (open) {
      setFormValue({});
      setErrorModel(undefined);
      setIdempotencyKey(newIdempotencyKey());
    }
  }, [open, action]);

  const handleClose = (event?: React.SyntheticEvent) => {
    setLoading(false);
    onClose?.(event as any);
  };

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) return;
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result = await postEnergyHubCredit(energyHubId, action, {
        amount: Number(formValue.amount),
        note: formValue.note || undefined,
        idempotencyKey,
      });
      if (result.success) {
        toastSuccessMessage('Credit updated');
        handleClose();
        onSubmitted?.();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right" open={open} onClose={handleClose} {...rest}>
      <Drawer.Header>
        <Drawer.Title>{titles[action]}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">Submit</Button>
          <Button onClick={handleClose} appearance="subtle">Cancel</Button>
        </Drawer.Actions>
      </Drawer.Header>
      <Drawer.Body>
        <Message type="info" showIcon style={{ marginBottom: 20 }}>
          Current balance: <b>{formatHkd(balance)}</b>.{' '}
          {action == 'TopUp' && 'Record money the venue paid us (FPS / bank transfer).'}
          {action == 'Refund' && 'Record unused credit paid back to the venue. Cannot exceed the balance.'}
          {action == 'Adjust' && 'Positive adds credit, negative removes it (e.g. goodwill, corrections).'}
        </Message>
        <Form name="energy-hub-credit-form" fluid ref={formRef}
          onChange={value => setFormValue(pre => ({ ...pre, ...value }))}
          formValue={formValue} model={buildSchema(action, balance)} disabled={loading}
        >
          <Form.Group>
            <Form.Label>Amount (HK$)</Form.Label>
            <Form.Control name="amount" accepter={NumberInput} step={1} />
          </Form.Group>
          <Form.Group>
            <Form.Label>{action == 'TopUp' ? 'Note / payment reference (optional)' : 'Reason'}</Form.Label>
            <Form.Control name="note" accepter={Textarea} rows={3} />
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }} />}
      </Drawer.Body>
    </Drawer>
  );
};

export default CreditDrawer;
